/**
 * DemoPage.tsx — live status-page demo
 *
 * Purpose:    Demo embed for ɳSentry status page (shows coming-soon while pre-launch)
 * Inputs:     none
 * Outputs:    iframe demo or coming-soon banner per DEMO_LIVE flag
 * Constraints: client-side only; iframe sandbox
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { useEffect, useRef, useState } from 'react'

type IframeState = 'loading' | 'loaded' | 'error'

const DEMO_LIVE = false

export function DemoPage() {
  const [iframeState, setIframeState] = useState<IframeState>('loading')
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe || !DEMO_LIVE) return

    const handleLoad = () => setIframeState('loaded')
    const handleError = () => setIframeState('error')
    iframe.addEventListener('load', handleLoad)
    iframe.addEventListener('error', handleError)

    const timeout = setTimeout(() => {
      if (iframeState === 'loading') setIframeState('error')
    }, 10_000)

    return () => {
      iframe.removeEventListener('load', handleLoad)
      iframe.removeEventListener('error', handleError)
      clearTimeout(timeout)
    }
  }, [iframeState])

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="mb-10 text-center">
        <h1 className="mb-3 text-4xl font-bold">Status page demo</h1>
        <p className="text-brand-muted">ɳSentry status pages are self-hosted on your nSelf server. This is what your users see.</p>
      </div>

      {!DEMO_LIVE ? (
        <ComingSoon />
      ) : iframeState === 'error' ? (
        <ErrorState onRetry={() => setIframeState('loading')} />
      ) : (
        <div className="relative overflow-hidden rounded-2xl border border-brand-subtle bg-brand-bg">
          {iframeState === 'loading' && <LoadingShimmer />}
          <iframe
            ref={iframeRef}
            src="https://status.nself.org"
            title="ɳSentry live status page"
            className={`h-[600px] w-full transition-opacity duration-300 ${iframeState === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      )}

      <div className="mt-10">
        <p className="mb-4 text-sm font-medium text-brand-muted">Simulated demo scenarios:</p>
        <div className="flex flex-wrap gap-3">
          <ScenarioPill label="All systems operational" color="green" description="No active incidents. All components passing." />
          <ScenarioPill label="Active incident" color="red" description="P1 incident open. Affected: API gateway." />
          <ScenarioPill label="Scheduled maintenance" color="yellow" description="Database cluster upgrade window 02:00–04:00 UTC." />
        </div>
      </div>

      <div className="mt-12 rounded-xl border border-sky-200 dark:border-sky-800/50 bg-sky-50 dark:bg-sky-950/20 p-6 text-center">
        <p className="mb-2 font-semibold text-brand-ink">This page runs on your server</p>
        <p className="mb-4 text-sm text-brand-muted">
          The status page plugin (port 3832) serves your public status page. Custom domain, component groups, incident history, subscriber notifications — all on your infra.
        </p>
        <a href="/self-host" className="text-sm font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300 hover:underline underline-offset-2">Read the self-host guide</a>
      </div>
    </div>
  )
}

function LoadingShimmer() {
  return (
    <div className="absolute inset-0 flex flex-col gap-4 p-6" role="status" aria-label="Loading demo...">
      <div className="h-8 w-48 animate-pulse rounded-md bg-brand-surface-2" />
      <div className="h-4 w-full animate-pulse rounded-md bg-brand-surface-2" />
      <div className="h-4 w-3/4 animate-pulse rounded-md bg-brand-surface-2" />
      <div className="mt-4 h-40 animate-pulse rounded-xl bg-brand-surface-2" />
      <div className="h-40 animate-pulse rounded-xl bg-brand-surface-2" />
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-[400px] flex-col items-center justify-center gap-4 rounded-2xl border border-brand-subtle bg-brand-bg">
      <p className="text-brand-muted">Could not load the demo.</p>
      <button onClick={onRetry} className="rounded-lg border border-brand-card px-4 py-2 text-sm text-brand-secondary hover:border-gray-500 hover:text-brand-ink">
        Try again
      </button>
    </div>
  )
}

function ComingSoon() {
  return (
    <div className="flex h-[400px] flex-col items-center justify-center gap-4 rounded-2xl border border-sky-200 dark:border-sky-800/40 bg-sky-50 dark:bg-sky-950/10">
      <p className="text-xl font-semibold">Demo available at launch</p>
      <p className="max-w-md text-center text-sm text-brand-muted">
        The live status page demo will be available when ɳSentry ships in v1.1.0. In the meantime, explore the plugin grid to see what&apos;s included.
      </p>
      <a href="/pricing" className="mt-2 rounded-lg bg-sky-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-sky-400">View pricing</a>
    </div>
  )
}

function ScenarioPill({ label, color, description }: { label: string; color: 'green' | 'red' | 'yellow'; description: string }) {
  const colorMap = {
    green: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400',
    red: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400',
    yellow: 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400',
  } as const
  return (
    <div className={`rounded-lg border px-4 py-3 ${colorMap[color]}`} title={description} role="note" aria-label={description}>
      <span className="text-sm font-medium">{label}</span>
    </div>
  )
}
